/**
 * Register Screen Component
 *
 * Provides name/email/password account creation.
 * Only rendered when VITE_ENABLE_REGISTER=TRUE.
 */

import { useState } from "react";
import { navigateTo } from "../../router";
import { Alert, Button, Card, Input } from "../../ui";
import { useAuth, useAuthActions } from "../store/auth-store";
import { GoogleLoginButton } from "./GoogleLoginButton";

export function RegisterScreen() {
  const { isLoading, error } = useAuth();
  const actions = useAuthActions();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const success = await actions.register(email, password, name);
    if (success) {
      navigateTo("/login");
    }
  };

  const handleGoogleSignup = async (idToken: string) => {
    const success = await actions.loginWithGoogle(idToken);
    if (success) {
      navigateTo("/");
    }
  };

  const errorLines = error ? error.split("\n") : [];

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-3 sm:p-6">
      <Card className="w-full shadow-lg sm:max-w-md sm:p-8">
        <h1 className="mb-2 text-center font-semibold text-2xl">회원가입</h1>
        <p className="mb-6 text-center text-sm text-text-secondary">
          새 계정을 만들어 게임을 시작하세요
        </p>

        {errorLines.length > 0 && (
          <Alert className="mb-4">
            {errorLines.length === 1 ? (
              errorLines[0]
            ) : (
              <ul className="list-disc space-y-1 pl-4">
                {errorLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            type="text"
            placeholder="이름"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading}
          />
          <Input
            type="email"
            placeholder="이메일"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="비밀번호"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              data-testid="toggle-password-btn"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-text-muted text-xs"
            >
              {showPassword ? "숨기기" : "보기"}
            </button>
          </div>
          <Button type="submit" data-testid="register-btn" disabled={isLoading}>
            {isLoading ? "가입 중..." : "회원가입"}
          </Button>
        </form>

        <div className="mt-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-border-subtle" />
          <span className="text-text-hint text-xs">또는</span>
          <div className="h-px flex-1 bg-border-subtle" />
        </div>

        <div className="mt-4">
          <GoogleLoginButton
            onSuccess={handleGoogleSignup}
            text="signup_with"
            disabled={isLoading}
          />
        </div>

        <p className="mt-4 text-center text-sm text-text-secondary">
          이미 계정이 있으신가요?{" "}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateTo("/login")}
            className="px-0 py-0"
          >
            로그인
          </Button>
        </p>
      </Card>
    </div>
  );
}
